import React, { useState, useEffect } from "react";
import { CSVDownload } from "react-csv";
import { Container, Card, CardContent, makeStyles } from "@material-ui/core";
import axios from "axios";
import Navigation from "./Navigation";

const useBoxStyles = makeStyles({
  box: {
    position: "relative",
    padding: 0,
    borderRadius: "20px",
    background: "linear-gradient(160deg, #111118 0%, #0c0c14 100%)",
    border: "1px solid rgba(255,255,255,0.08)",
    boxShadow:
      "0 4px 32px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.05)",
    minWidth: 280,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    transition:
      "box-shadow 0.4s ease, border-color 0.4s ease, transform 0.4s cubic-bezier(0.22,1,0.36,1)",
    animation: "fadeIn 0.6s cubic-bezier(0.22,1,0.36,1) backwards",
    willChange: "transform",
    "&:hover": {
      transform: "translateY(-6px) scale(1.01)",
      // ✓ Works because useBoxStyles({ role }) is called inside ExhibitorCard
      boxShadow: ({ role }) =>
        role === "Master"
          ? "0 12px 40px rgba(245,158,11,0.5), inset 0 1px 0 rgba(255,255,255,0.07)"
          : role === "Session"
            ? "0 12px 40px rgba(94,148,200,0.5), inset 0 1px 0 rgba(255,255,255,0.07)"
            : "0 12px 40px rgba(232,27,35,0.25), inset 0 1px 0 rgba(255,255,255,0.07)",
      borderColor: ({ role }) =>
        role === "Master"
          ? "rgba(245,158,11,0.6)"
          : role === "Session"
            ? "rgba(94,148,200,0.6)"
            : "rgba(232,27,35,0.6)",
    },
  },
});

/* ══════════════════════════════════════════════════════════════════
   ExhibitorCard
══════════════════════════════════════════════════════════════════ */
const ExhibitorCard = ({ classes, exhibitor, index, csvData, onReport }) => {
  // ✓ role prop passed here where exhibitor is in scope
  const boxClasses = useBoxStyles({ role: exhibitor?.role });

  return (
    <div
      className={boxClasses.box}
      style={{
        animationDelay: `${0.1 + index * 0.1}s`,
        width: exhibitor?.role === "Master" ? "88%" : "calc(45% - 24px)",
        // ✗ "&:hover" key removed — inline style never supports pseudo-selectors
      }}
    >
      {/* Shimmer header */}
      <div
        className={classes.boxHeader}
        style={{
          background:
            exhibitor?.role === "Master"
              ? "linear-gradient(90deg, yellow 0%, gold 50%, orange 100%)"
              : "linear-gradient(90deg, #161620 0%, #1a1a28 50%, #161620 100%)",
        }}
      >
        <div className={classes.boxHeaderShimmer} />
        <span
          className={classes.boxHeaderLabel}
          style={{
            color:
              exhibitor?.role === "Master"
                ? "transparent"
                : exhibitor?.role === "Session"
                  ? "#5e94c8"
                  : "#e81b23",
          }}
        >
          {exhibitor?.role || "—"}
        </span>
        <div className={classes.boxHeaderNames}>
          <span
            className={classes.boxHeaderName}
            style={{ color: exhibitor?.role === "Master" ? "black" : "Gold" }}
          >
            {exhibitor?.name || "—"}
          </span>
          <span className={classes.boxHeaderValue}>
            {exhibitor?.email || "—"}
          </span>
        </div>
      </div>

      {/* Stats */}
      <div className={classes.statsRow}>
        <div className={classes.statItem}>
          <span className={classes.statValue}>
            {exhibitor?.totalDocuments ?? "—"}
          </span>
          <span className={classes.statLabel}>Total Users</span>
        </div>
        <div className={classes.statDivider} />
        <div className={classes.statItem}>
          <span
            className={classes.statValue}
            style={{
              color:
                exhibitor?.role === "Master"
                  ? "Gold"
                  : exhibitor?.role === "Session"
                    ? "#5e94c8"
                    : "#e81b23",
            }}
          >
            {exhibitor?.checkedInUsers ?? exhibitor?.CheckedInUsers ?? 0}
          </span>
          <span className={classes.statLabel}>Checked In</span>
        </div>
      </div>

      {/* Progress bar */}
      {exhibitor && (
        <div className={classes.progressWrapper}>
          <div className={classes.progressTrack}>
            <div
              className={classes.progressFill}
              style={{
                width: `${Math.min(100, Math.round(((exhibitor?.checkedInUsers ?? exhibitor?.CheckedInUsers ?? 0) / (exhibitor?.totalDocuments || 1)) * 100))}%`,
                background:
                  exhibitor?.role === "Master"
                    ? "linear-gradient(90deg, yellow, gold)"
                    : exhibitor?.role === "Session"
                      ? "linear-gradient(90deg, #5e94c8, #7fb8e0)"
                      : "linear-gradient(90deg, #e81b23, #ff4d55)",
              }}
            />
          </div>
          <span className={classes.progressLabel}>
            {Math.round(
              ((exhibitor?.checkedInUsers ?? exhibitor?.CheckedInUsers ?? 0) /
                (exhibitor?.totalDocuments || 1)) *
                100,
            )}
            % attendance
          </span>
        </div>
      )}

      {/* Report button */}
      <div
        className={classes.genReportbtn}
        onClick={onReport}
        style={{
          background:
            exhibitor?.role === "Master"
              ? "Gold"
              : exhibitor?.role === "Session"
                ? "#5e94c8"
                : "#e81b23",
          color: exhibitor?.role === "Master" ? "black" : "white",
        }}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          style={{ marginRight: 8 }}
        >
          <path
            d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        Generate Report
        {csvData[index]?.length ? (
          <CSVDownload data={csvData[index]} target="_blank" />
        ) : null}
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════
   Analytics — main component
══════════════════════════════════════════════════════════════════ */
const Analytics = () => {
  const [csvData, setCsvData] = useState([]);
  const [dataArray, setDataArray] = useState([]);
  const [dataArray2, setDataArray2] = useState([]);
  const [variant, setVariant] = useState(false);
  const [activeRoleFilter, setActiveRoleFilter] = useState("All");

  const token = JSON.parse(localStorage.getItem("token")).token;

  // ✓ No role prop — Analytics has no exhibitor variable
  const classes = useStyles();

  const handleDownloadCsv = async (index) => {
    try {
      const tokenid = dataArray[index].id;
      const response = await axios.get(
        `${process.env.REACT_APP_ADMIN}/qrCode/getAllScannedUsers?exhibitor=${tokenid}`,
      );
      setCsvData((prev) => {
        const n = [...prev];
        n[index] = response.data;
        return n;
      });
      setTimeout(() => {
        setCsvData((prev) => {
          const n = [...prev];
          n[index] = [];
          return n;
        });
      }, 500);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_ADMIN}/qrCode/checkindata?token=${token}`,
        );
        setDataArray(response.data.exhibitorDataArray);
        setVariant(response.data.variant !== "Ordinary");
      } catch (error) {
        console.error(error);
      }
    };
    const poll = setInterval(fetchData, 10000);
    fetchData();
    return () => clearInterval(poll);
  }, [token]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_ADMIN}/qrCode/getAllDataZoneCount`,
        );
        setDataArray2(response.data);
      } catch (error) {
        console.error(error);
      }
    };
    const poll = setInterval(fetchData, 10000);
    fetchData();
    return () => clearInterval(poll);
  }, [token]);

  const zones = [
    {
      label: "Green Zone",
      key: "Green",
      color: "#22c55e",
      glow: "rgba(34,197,94,0.25)",
    },
    {
      label: "Yellow Zone",
      key: "Yellow",
      color: "#eab308",
      glow: "rgba(234,179,8,0.25)",
    },
    {
      label: "Purple Zone",
      key: "Purple",
      color: "#a855f7",
      glow: "rgba(168,85,247,0.25)",
    },
    {
      label: "Pink Zone",
      key: "Pink",
      color: "#ec4899",
      glow: "rgba(236,72,153,0.25)",
    },
    {
      label: "Blue Zone",
      key: "Blue",
      color: "#3b82f6",
      glow: "rgba(59,130,246,0.25)",
    },
  ];

  const uniqueRoles = React.useMemo(() => {
    if (!dataArray?.length) return [];
    return [...new Set(dataArray.map((e) => e?.role).filter(Boolean))];
  }, [dataArray]);

  const filteredDataArray = React.useMemo(() => {
    if (!dataArray) return [];
    if (activeRoleFilter === "All") return dataArray;
    return dataArray.filter((e) => e?.role === activeRoleFilter);
  }, [dataArray, activeRoleFilter]);

  const roleColor = (role) => {
    if (role === "Master")
      return {
        color: "#f59e0b",
        border: "rgba(245,158,11,0.4)",
        bg: "rgba(245,158,11,0.1)",
      };
    if (role === "Session")
      return {
        color: "#5e94c8",
        border: "rgba(94,148,200,0.4)",
        bg: "rgba(94,148,200,0.1)",
      };
    return {
      color: "#e81b23",
      border: "rgba(232,27,35,0.4)",
      bg: "rgba(232,27,35,0.1)",
    };
  };

  return (
    <Container className={classes.container}>
      <div className={classes.bgOrb1} />
      <div className={classes.bgOrb2} />
      <div className={classes.bgOrb3} />

      <Card className={classes.card}>
        <CardContent style={{ padding: 0 }}>
          <Navigation />

          <div className={classes.contentWrapper}>
            {/* Page heading */}
            <div className={classes.pageHeader}>
              <div className={classes.pageHeaderIcon}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M3 3v18h18"
                    stroke="#e81b23"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <path
                    d="M7 16l4-5 4 3 4-6"
                    stroke="#e81b23"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div>
                <h2 className={classes.pageTitle}>Analytics</h2>
                <p className={classes.pageSubtitle}>
                  Live check-in data — refreshes every 10s
                </p>
              </div>
              <div className={classes.liveBadge}>
                <span className={classes.liveDot} />
                LIVE
              </div>
            </div>

            {/* Role filter bar */}
            {variant && uniqueRoles.length > 0 && (
              <div className={classes.filterBar}>
                <div className={classes.filterBarInner}>
                  <svg
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                    style={{ flexShrink: 0, opacity: 0.5 }}
                  >
                    <path
                      d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z"
                      stroke="rgba(180,180,200,0.7)"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span className={classes.filterBarLabel}>Filter by role</span>

                  <button
                    className={classes.filterPill}
                    onClick={() => setActiveRoleFilter("All")}
                    style={
                      activeRoleFilter === "All"
                        ? {
                            background: "rgba(232,27,35,0.15)",
                            border: "1px solid rgba(232,27,35,0.55)",
                            color: "#e81b23",
                            boxShadow: "0 0 12px rgba(232,27,35,0.2)",
                          }
                        : {}
                    }
                  >
                    All
                    <span
                      className={classes.filterPillCount}
                      style={
                        activeRoleFilter === "All"
                          ? {
                              background: "rgba(232,27,35,0.25)",
                              color: "#e81b23",
                            }
                          : {}
                      }
                    >
                      {dataArray.length}
                    </span>
                  </button>

                  {uniqueRoles.map((role) => {
                    const rc = roleColor(role);
                    const count = dataArray.filter(
                      (e) => e?.role === role,
                    ).length;
                    const isActive = activeRoleFilter === role;
                    return (
                      <button
                        key={role}
                        className={classes.filterPill}
                        onClick={() => setActiveRoleFilter(role)}
                        style={
                          isActive
                            ? {
                                background: rc.bg,
                                border: `1px solid ${rc.border}`,
                                color: rc.color,
                                boxShadow: `0 0 12px ${rc.bg}`,
                              }
                            : {}
                        }
                      >
                        <span
                          className={classes.filterPillDot}
                          style={{ background: rc.color }}
                        />
                        {role}
                        <span
                          className={classes.filterPillCount}
                          style={
                            isActive
                              ? { background: rc.bg, color: rc.color }
                              : {}
                          }
                        >
                          {count}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Exhibitor cards */}
            {!variant ? (
              <div className={classes.boxWrapper}>
                <ExhibitorCard
                  classes={classes}
                  exhibitor={dataArray[0] ?? null}
                  index={0}
                  csvData={csvData}
                  onReport={() => handleDownloadCsv(0)}
                />
              </div>
            ) : (
              <div className={classes.boxWrapper}>
                {filteredDataArray.length > 0 ? (
                  filteredDataArray.map((exhibitor, index) => {
                    const originalIndex = dataArray.indexOf(exhibitor);
                    return (
                      <ExhibitorCard
                        key={originalIndex}
                        classes={classes}
                        exhibitor={exhibitor}
                        index={index}
                        csvData={csvData}
                        onReport={() => handleDownloadCsv(originalIndex)}
                      />
                    );
                  })
                ) : (
                  <div className={classes.emptyState}>
                    <svg
                      width="36"
                      height="36"
                      viewBox="0 0 24 24"
                      fill="none"
                      style={{ opacity: 0.3, marginBottom: 12 }}
                    >
                      <circle
                        cx="11"
                        cy="11"
                        r="8"
                        stroke="rgba(180,180,200,0.6)"
                        strokeWidth="1.5"
                      />
                      <path
                        d="M21 21l-4.35-4.35"
                        stroke="rgba(180,180,200,0.6)"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                    <span className={classes.emptyStateText}>
                      No exhibitors match this role
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Zone section */}
            <div className={classes.sectionLabel}>
              <span className={classes.sectionLabelLine} />
              <span className={classes.sectionLabelText}>Zone Breakdown</span>
              <span className={classes.sectionLabelLine} />
            </div>

            <div className={classes.zoneWrapper}>
              {zones.map((zone, i) => (
                <div
                  key={zone.key}
                  className={classes.zoneCard}
                  style={{
                    "--zone-color": zone.color,
                    "--zone-glow": zone.glow,
                    animationDelay: `${0.05 + i * 0.07}s`,
                  }}
                >
                  <div
                    className={classes.zoneBar}
                    style={{
                      background: zone.color,
                      boxShadow: `0 0 12px ${zone.glow}`,
                    }}
                  />
                  <div
                    className={classes.zoneCount}
                    style={{ color: zone.color }}
                  >
                    {dataArray2?.[zone.key] ?? 0}
                  </div>
                  <div className={classes.zoneLabel}>{zone.label}</div>
                  <div
                    className={classes.zoneGlow}
                    style={{
                      background: `radial-gradient(ellipse, ${zone.glow} 0%, transparent 70%)`,
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </Container>
  );
};

/* ══════════════════════════════════════════════════════════════════
   Shared styles — layout only, no role prop needed
══════════════════════════════════════════════════════════════════ */
const useStyles = makeStyles((theme) => ({
  "@keyframes fadeIn": {
    from: { opacity: 0, transform: "translateY(28px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  "@keyframes slideDown": {
    from: { opacity: 0, transform: "translateY(-20px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  "@keyframes orbFloat1": {
    "0%,100%": { transform: "translate(0,0) scale(1)" },
    "33%": { transform: "translate(60px,-80px) scale(1.15)" },
    "66%": { transform: "translate(-40px,40px) scale(0.9)" },
  },
  "@keyframes orbFloat2": {
    "0%,100%": { transform: "translate(0,0) scale(1)" },
    "33%": { transform: "translate(-70px,50px) scale(1.1)" },
    "66%": { transform: "translate(50px,-60px) scale(0.95)" },
  },
  "@keyframes orbFloat3": {
    "0%,100%": { transform: "translate(0,0) scale(1)" },
    "50%": { transform: "translate(40px,60px) scale(1.2)" },
  },
  "@keyframes headerShimmer": {
    "0%": { backgroundPosition: "-200% center" },
    "100%": { backgroundPosition: "200% center" },
  },
  "@keyframes livePulse": {
    "0%,100%": { opacity: 1, transform: "scale(1)" },
    "50%": { opacity: 0.3, transform: "scale(0.85)" },
  },
  "@keyframes progressGrow": { from: { width: "0% !important" }, to: {} },
  "@keyframes countUp": {
    from: { opacity: 0, transform: "translateY(8px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  "@keyframes filterFadeIn": {
    from: { opacity: 0, transform: "translateY(-8px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },

  container: {
    background: "linear-gradient(160deg,#0a0a0f 0%,#12121a 40%,#0d0d15 100%)",
    height: "100vh",
    width: "100vw",
    maxWidth: "100vw",
    position: "relative",
    padding: "0 !important",
    overflow: "hidden",
  },

  bgOrb1: {
    position: "fixed",
    top: "-15%",
    left: "-10%",
    width: 600,
    height: 600,
    borderRadius: "50%",
    background:
      "radial-gradient(circle,rgba(232,27,35,0.18) 0%,rgba(232,27,35,0.04) 50%,transparent 70%)",
    animation: "$orbFloat1 12s ease-in-out infinite",
    pointerEvents: "none",
    zIndex: 0,
    filter: "blur(8px)",
  },
  bgOrb2: {
    position: "fixed",
    bottom: "-20%",
    right: "-10%",
    width: 700,
    height: 700,
    borderRadius: "50%",
    background:
      "radial-gradient(circle,rgba(180,10,18,0.14) 0%,rgba(120,0,10,0.05) 50%,transparent 70%)",
    animation: "$orbFloat2 16s ease-in-out infinite",
    pointerEvents: "none",
    zIndex: 0,
    filter: "blur(12px)",
  },
  bgOrb3: {
    position: "fixed",
    top: "40%",
    left: "30%",
    width: 400,
    height: 400,
    borderRadius: "50%",
    background:
      "radial-gradient(circle,rgba(255,80,80,0.06) 0%,transparent 70%)",
    animation: "$orbFloat3 20s ease-in-out infinite",
    pointerEvents: "none",
    zIndex: 0,
    filter: "blur(20px)",
  },

  card: {
    background: "rgba(14,14,20,0.88)",
    backdropFilter: "blur(30px)",
    WebkitBackdropFilter: "blur(30px)",
    border: "1px solid rgba(255,255,255,0.06)",
    boxShadow:
      "0 8px 64px rgba(0,0,0,0.6),inset 0 1px 0 rgba(255,255,255,0.05)",
    height: "99.6%",
    margin: "0 !important",
    padding: "0 !important",
    overflowY: "scroll",
    position: "relative",
    zIndex: 1,
    animation: "$fadeIn 0.7s cubic-bezier(0.22,1,0.36,1)",
    "&::-webkit-scrollbar": { width: "6px" },
    "&::-webkit-scrollbar-track": {
      background: "rgba(255,255,255,0.03)",
      borderRadius: "10px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "linear-gradient(180deg,#e81b23 0%,#7b0008 100%)",
      borderRadius: "10px",
    },
  },

  contentWrapper: {
    padding: theme.spacing(4),
    [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
  },

  pageHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(4),
    animation: "$fadeIn 0.5s cubic-bezier(0.22,1,0.36,1)",
  },
  pageHeaderIcon: {
    width: 48,
    height: 48,
    borderRadius: "12px",
    background: "rgba(232,27,35,0.1)",
    border: "1px solid rgba(232,27,35,0.3)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  pageTitle: {
    margin: 0,
    fontSize: "1.8rem",
    fontWeight: 800,
    color: "#f0f0f5",
    letterSpacing: "0.03em",
    textShadow: "0 0 30px rgba(232,27,35,0.4)",
    lineHeight: 1.1,
  },
  pageSubtitle: {
    margin: "4px 0 0",
    fontSize: "0.82rem",
    color: "rgba(180,180,200,0.45)",
    letterSpacing: "0.06em",
  },
  liveBadge: {
    marginLeft: "auto",
    display: "flex",
    alignItems: "center",
    gap: 6,
    background: "rgba(232,27,35,0.1)",
    border: "1px solid rgba(232,27,35,0.3)",
    borderRadius: "20px",
    padding: "5px 12px",
    fontSize: "0.7rem",
    fontWeight: 700,
    color: "#e81b23",
    letterSpacing: "0.1em",
  },
  liveDot: {
    width: 7,
    height: 7,
    borderRadius: "50%",
    background: "#e81b23",
    animation: "$livePulse 1.5s ease-in-out infinite",
    display: "inline-block",
  },

  filterBar: {
    marginBottom: theme.spacing(3),
    animation: "$filterFadeIn 0.4s cubic-bezier(0.22,1,0.36,1)",
  },
  filterBarInner: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: "10px 16px",
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.07)",
    borderRadius: "14px",
    flexWrap: "wrap",
    backdropFilter: "blur(8px)",
  },
  filterBarLabel: {
    fontSize: "0.68rem",
    fontWeight: 600,
    color: "rgba(180,180,200,0.4)",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    marginRight: 4,
    whiteSpace: "nowrap",
  },
  filterPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "5px 12px",
    borderRadius: "20px",
    border: "1px solid rgba(255,255,255,0.1)",
    background: "transparent",
    color: "rgba(180,180,200,0.55)",
    fontSize: "0.75rem",
    fontWeight: 600,
    letterSpacing: "0.04em",
    cursor: "pointer",
    transition: "all 0.25s cubic-bezier(0.22,1,0.36,1)",
    outline: "none",
    whiteSpace: "nowrap",
    "&:hover": {
      background: "rgba(255,255,255,0.06)",
      color: "rgba(240,240,245,0.85)",
      borderColor: "rgba(255,255,255,0.2)",
      transform: "translateY(-1px)",
    },
  },
  filterPillDot: { width: 6, height: 6, borderRadius: "50%", flexShrink: 0 },
  filterPillCount: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 18,
    height: 18,
    borderRadius: "10px",
    background: "rgba(255,255,255,0.08)",
    color: "rgba(180,180,200,0.5)",
    fontSize: "0.65rem",
    fontWeight: 700,
    padding: "0 5px",
    transition: "all 0.25s ease",
  },

  boxWrapper: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    alignItems: "flex-start",
    gap: theme.spacing(3),
    padding: theme.spacing(1, 0, 3),
    animation: "$fadeIn 0.5s ease-out",
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "60px 20px",
    width: "100%",
    animation: "$fadeIn 0.4s ease-out",
  },
  emptyStateText: {
    fontSize: "0.85rem",
    color: "rgba(180,180,200,0.3)",
    letterSpacing: "0.06em",
    fontWeight: 500,
  },

  // box lives in useBoxStyles — not here

  boxHeader: {
    position: "relative",
    overflow: "hidden",
    padding: "20px 24px 16px",
    borderBottom: "1px solid rgba(255,255,255,0.06)",
    backgroundSize: "200% 100%",
    animation: "$headerShimmer 5s linear infinite",
  },
  boxHeaderShimmer: {
    position: "absolute",
    inset: 0,
    background:
      "linear-gradient(90deg,transparent 0%,rgba(232,27,35,0.06) 50%,transparent 100%)",
    backgroundSize: "200% 100%",
    animation: "$headerShimmer 4s linear infinite",
    pointerEvents: "none",
  },
  boxHeaderLabel: {
    display: "block",
    fontSize: "0.68rem",
    fontWeight: 600,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    marginBottom: 4,
  },
  boxHeaderNames: {
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
  },
  boxHeaderName: {
    display: "block",
    fontSize: "1.1rem",
    fontWeight: 700,
    color: "Gold",
    letterSpacing: "0.02em",
    animation: "$countUp 0.5s ease-out",
  },
  boxHeaderValue: {
    display: "block",
    fontSize: "1rem",
    fontWeight: 600,
    color: "#f0f0f5",
    letterSpacing: "0.02em",
    textShadow: "0 0 4px rgba(0,0,0,0.9)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  statsRow: {
    display: "flex",
    alignItems: "stretch",
    padding: "20px 24px",
    gap: 0,
  },
  statItem: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 4,
  },
  statValue: {
    fontSize: "2.2rem",
    fontWeight: 800,
    color: "#f0f0f5",
    lineHeight: 1,
    letterSpacing: "-0.02em",
    animation: "$countUp 0.5s ease-out",
  },
  statLabel: {
    fontSize: "0.72rem",
    color: "rgba(180,180,200,0.5)",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    fontWeight: 500,
  },
  statDivider: {
    width: "1px",
    background: "rgba(255,255,255,0.08)",
    margin: "0 16px",
    alignSelf: "stretch",
  },

  progressWrapper: {
    padding: "0 24px 20px",
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  progressTrack: {
    width: "100%",
    height: 4,
    borderRadius: 4,
    background: "rgba(255,255,255,0.07)",
    overflow: "hidden",
  },
  progressFill: {
    height: "100%",
    borderRadius: 4,
    boxShadow: "0 0 8px rgba(232,27,35,0.6)",
    transition: "width 1s cubic-bezier(0.22,1,0.36,1)",
    animation: "$progressGrow 1s cubic-bezier(0.22,1,0.36,1)",
  },
  progressLabel: {
    fontSize: "0.7rem",
    color: "rgba(180,180,200,0.4)",
    letterSpacing: "0.05em",
    textAlign: "right",
  },

  genReportbtn: {
    margin: "0 24px 24px",
    padding: "11px 22px",
    alignSelf: "flex-start",
    borderRadius: "10px",
    color: "#fff",
    fontSize: "0.88rem",
    fontWeight: 700,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    letterSpacing: "0.02em",
    boxShadow: "0 4px 16px rgba(155, 155, 155, 0.35)",
    transition: "all 0.3s cubic-bezier(0.22,1,0.36,1)",
    position: "relative",
    overflow: "hidden",
    "&::after": {
      content: '""',
      position: "absolute",
      inset: 0,
      background:
        "linear-gradient(90deg,transparent,rgba(255,255,255,0.15),transparent)",
      backgroundSize: "200% 100%",
      backgroundPosition: "-200% center",
      transition: "background-position 0.4s ease",
    },
    "&:hover": {
      transform: "translateY(-2px) scale(1.02)",
      boxShadow: "0 8px 24px rgba(157, 157, 157, 0.5)",
      "&::after": { backgroundPosition: "200% center" },
    },
    "&:active": { transform: "translateY(0) scale(1)" },
  },

  sectionLabel: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    margin: theme.spacing(2, 0, 3),
    padding: "0 4px",
  },
  sectionLabelLine: {
    flex: 1,
    height: "1px",
    background:
      "linear-gradient(90deg,transparent,rgba(232,27,35,0.3),transparent)",
  },
  sectionLabelText: {
    fontSize: "0.72rem",
    fontWeight: 700,
    color: "rgba(232,27,35,0.7)",
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  },

  zoneWrapper: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: theme.spacing(2),
    paddingBottom: theme.spacing(4),
  },
  zoneCard: {
    position: "relative",
    width: "calc(20% - 16px)",
    minWidth: 140,
    borderRadius: "16px",
    background: "linear-gradient(160deg,#111118 0%,#0c0c14 100%)",
    border: "1px solid rgba(255,255,255,0.07)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    paddingBottom: 20,
    transition:
      "transform 0.4s cubic-bezier(0.22,1,0.36,1),box-shadow 0.4s ease,border-color 0.4s ease",
    animation: "$fadeIn 0.6s cubic-bezier(0.22,1,0.36,1) backwards",
    willChange: "transform",
    "&:hover": {
      transform: "translateY(-6px) scale(1.03)",
      borderColor: "var(--zone-color,rgba(255,255,255,0.2))",
      boxShadow: "0 8px 32px var(--zone-glow,rgba(0,0,0,0.3))",
    },
    [theme.breakpoints.down("sm")]: { width: "calc(50% - 8px)" },
  },
  zoneBar: {
    width: "100%",
    height: 4,
    marginBottom: 20,
    transition: "box-shadow 0.4s ease",
  },
  zoneCount: {
    fontSize: "2.8rem",
    fontWeight: 800,
    lineHeight: 1,
    letterSpacing: "-0.02em",
    animation: "$countUp 0.6s ease-out",
    position: "relative",
    zIndex: 1,
  },
  zoneLabel: {
    marginTop: 8,
    fontSize: "0.7rem",
    fontWeight: 600,
    color: "rgba(180,180,200,0.5)",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    position: "relative",
    zIndex: 1,
  },
  zoneGlow: {
    position: "absolute",
    bottom: 0,
    left: "50%",
    transform: "translateX(-50%)",
    width: "140%",
    height: "60%",
    pointerEvents: "none",
    zIndex: 0,
  },

  title: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    background: "rgba(14,14,20,0.9)",
    color: "#fff",
    padding: 20,
    marginBottom: 10,
    boxShadow: "0 4px 20px rgba(0,0,0,0.4)",
    animation: "$slideDown 0.5s cubic-bezier(0.22,1,0.36,1)",
  },
}));

export default Analytics;
